import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import * as Sentry from "@sentry/react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Mail, MapPin, Phone, CheckCircle2, ArrowRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { toast } from "sonner";

import { SeoHead } from "@/components/seo/SeoHead";
import { supabase } from "@/integrations/supabase/client";
import { buildContactPointJsonLd, buildFAQPageJsonLd } from "@/lib/seo";
import { useLocale, intlLocale, toLocale, dbLocaleSuffix } from "@/lib/locale";

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const SUBJECT_VALUES = ["demo", "enterprise", "press", "partnership", "support", "other"] as const;
const ROLE_VALUES = [
  "editor_in_chief",
  "publisher",
  "agency",
  "press_group",
  "marketing",
  "developer",
  "journalist",
  "other",
] as const;

type SubjectValue = (typeof SUBJECT_VALUES)[number];
type RoleValue = (typeof ROLE_VALUES)[number];

const SUBJECT_QUERY_MAP: Record<string, SubjectValue> = {
  demo: "demo",
  démo: "demo",
  demo_request: "demo",
  enterprise: "enterprise",
  ent: "enterprise",
  press: "press",
  presse: "press",
  partnership: "partnership",
  partenariat: "partnership",
  support: "support",
  other: "other",
  autre: "other",
};

const FAQ_KEYS = ["responseTime", "languages", "pricing", "rgpd", "press"] as const;
const WHY_KEYS = ["personalDemo", "customQuote", "partnership"] as const;

const formSchema = z.object({
  name: z.string().trim().min(1).max(120),
  email: z.string().trim().email().max(254),
  company: z.string().trim().max(180).optional().default(""),
  role: z.enum(ROLE_VALUES).optional(),
  subject: z.enum(SUBJECT_VALUES),
  message: z.string().trim().min(10).max(5000),
  gdpr: z.boolean().refine((v) => v === true, { message: "required" }),
  // Honeypot — must stay empty client-side.
  website: z.string().max(0).optional().default(""),
});

type FormValues = z.infer<typeof formSchema>;

function honeypotStyle(): React.CSSProperties {
  // Visually hidden without display:none — sophisticated bots flag the
  // display:none pattern as a trap. opacity 0 + off-screen + aria-hidden +
  // tabIndex=-1 reads as "ordinary field" to a naive crawler.
  return {
    position: "absolute",
    left: "-9999px",
    top: "-9999px",
    width: "1px",
    height: "1px",
    opacity: 0,
    pointerEvents: "none",
  };
}

export default function ContactPage() {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const [submitted, setSubmitted] = useState(false);

  const initialSubject = useMemo<SubjectValue>(() => {
    const raw = (searchParams.get("subject") ?? "").toLowerCase();
    return SUBJECT_QUERY_MAP[raw] ?? "demo";
  }, [searchParams]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      company: "",
      role: undefined,
      subject: initialSubject,
      message: "",
      gdpr: false,
      website: "",
    },
  });

  useEffect(() => {
    form.setValue("subject", initialSubject);
  }, [initialSubject, form]);

  const submitting = form.formState.isSubmitting;

  async function onSubmit(values: FormValues) {
    Sentry.addBreadcrumb({
      category: "user.action",
      type: "user.action",
      level: "info",
      message: "contact.form.submit",
      data: { subject: values.subject, locale: i18n.language },
    });
    try {
      const { error } = await supabase.functions.invoke("marketing-contact-submit", {
        body: {
          name: values.name,
          email: values.email,
          company: values.company || undefined,
          role: values.role,
          subject: values.subject,
          message: values.message,
          locale: toLocale(i18n.language),
          website: values.website,
        },
      });
      if (error) throw error;
      setSubmitted(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : t("marketing.contact.form.genericError");
      toast.error(message);
    }
  }

  const contactPointJsonLd = buildContactPointJsonLd({
    email: "contact@delapresse.fr",
    telephone: "+33 1 23 45 67 89",
    contactType: "customer support",
    areaServed: ["FR", "EU"],
    availableLanguage: ["fr", "en"],
    url: "/contact",
  });
  const faqJsonLd = buildFAQPageJsonLd(
    FAQ_KEYS.map((k) => ({
      question: t(`marketing.contact.faq.items.${k}.q`),
      answer: t(`marketing.contact.faq.items.${k}.a`),
    })),
  );

  return (
    <div className="min-h-screen bg-background">
      <SeoHead
        title={t("marketing.contact.seoTitle")}
        description={t("marketing.contact.seoDescription")}
        path="/contact"
        jsonLd={[contactPointJsonLd, faqJsonLd]}
      />
      <motion.div
        className="mx-auto max-w-6xl px-5 py-12 md:py-20"
        initial="hidden"
        animate="visible"
        variants={fadeInUp}
      >
        <header className="mb-12 text-center">
          <p className="dateline mb-3">{t("marketing.contact.eyebrow")}</p>
          <h1 className="font-serif text-4xl font-bold text-foreground md:text-5xl">
            {t("marketing.contact.heading")}
          </h1>
          <div className="mx-auto my-5 h-px w-16 bg-foreground/40" />
          <p className="mx-auto max-w-2xl text-base leading-relaxed text-muted-foreground">
            {t("marketing.contact.intro")}
          </p>
        </header>

        <div className="grid gap-10 lg:grid-cols-12">
          {/* Form column */}
          <div className="lg:col-span-7">
            {submitted ? (
              <SuccessCard onReset={() => setSubmitted(false)} />
            ) : (
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="rounded-lg border border-border bg-card p-6 md:p-8"
                noValidate
              >
                <h2 className="font-serif text-2xl font-semibold text-foreground">
                  {t("marketing.contact.form.heading")}
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  {t("marketing.contact.form.lead")}
                </p>

                <div className="mt-6 grid gap-5 sm:grid-cols-2">
                  <Field label={t("marketing.contact.form.nameLabel")} htmlFor="name">
                    <Input
                      id="name"
                      autoComplete="name"
                      maxLength={120}
                      placeholder={t("marketing.contact.form.namePlaceholder")}
                      aria-invalid={!!form.formState.errors.name}
                      {...form.register("name")}
                    />
                    <FieldError message={form.formState.errors.name?.message} />
                  </Field>

                  <Field label={t("marketing.contact.form.emailLabel")} htmlFor="email">
                    <Input
                      id="email"
                      type="email"
                      autoComplete="email"
                      placeholder={t("marketing.contact.form.emailPlaceholder")}
                      aria-invalid={!!form.formState.errors.email}
                      {...form.register("email")}
                    />
                    <FieldError message={form.formState.errors.email?.message} />
                  </Field>

                  <Field label={t("marketing.contact.form.companyLabel")} htmlFor="company">
                    <Input
                      id="company"
                      autoComplete="organization"
                      maxLength={180}
                      placeholder={t("marketing.contact.form.companyPlaceholder")}
                      {...form.register("company")}
                    />
                  </Field>

                  <Field label={t("marketing.contact.form.roleLabel")} htmlFor="role">
                    <Select
                      value={form.watch("role") ?? ""}
                      onValueChange={(v) => form.setValue("role", v as RoleValue)}
                    >
                      <SelectTrigger id="role">
                        <SelectValue placeholder={t("marketing.contact.form.rolePlaceholder")} />
                      </SelectTrigger>
                      <SelectContent>
                        {ROLE_VALUES.map((r) => (
                          <SelectItem key={r} value={r}>
                            {t(`marketing.contact.form.roleOptions.${r}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>

                <div className="mt-5">
                  <Field label={t("marketing.contact.form.subjectLabel")} htmlFor="subject">
                    <Select
                      value={form.watch("subject")}
                      onValueChange={(v) => form.setValue("subject", v as SubjectValue)}
                    >
                      <SelectTrigger id="subject">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SUBJECT_VALUES.map((s) => (
                          <SelectItem key={s} value={s}>
                            {t(`marketing.contact.form.subjectOptions.${s}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>

                <div className="mt-5">
                  <Field label={t("marketing.contact.form.messageLabel")} htmlFor="message">
                    <Textarea
                      id="message"
                      className="min-h-[140px]"
                      maxLength={5000}
                      placeholder={t("marketing.contact.form.messagePlaceholder")}
                      aria-invalid={!!form.formState.errors.message}
                      {...form.register("message")}
                    />
                    <FieldError message={form.formState.errors.message?.message} />
                  </Field>
                </div>

                <div className="mt-5 flex items-start gap-3">
                  <Checkbox
                    id="gdpr"
                    checked={form.watch("gdpr")}
                    onCheckedChange={(v) => form.setValue("gdpr", v === true, { shouldValidate: true })}
                    aria-invalid={!!form.formState.errors.gdpr}
                  />
                  <Label
                    htmlFor="gdpr"
                    className="text-xs font-normal leading-5 text-muted-foreground"
                  >
                    {t("marketing.contact.form.gdprNotice")}{" "}
                    <Link to="/politique-rgpd" className="text-primary hover:underline">
                      {t("marketing.contact.form.gdprLink")}
                    </Link>
                    .
                  </Label>
                </div>
                {form.formState.errors.gdpr && (
                  <p className="mt-1 text-xs text-destructive">
                    {t("marketing.contact.form.gdprRequired")}
                  </p>
                )}

                {/* Honeypot — invisible to humans, autofilled by naive bots. */}
                <div aria-hidden="true" style={honeypotStyle()}>
                  <label htmlFor="website">Website</label>
                  <input
                    id="website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    {...form.register("website")}
                  />
                </div>

                <Button type="submit" disabled={submitting} className="mt-6 w-full">
                  {submitting
                    ? t("marketing.contact.form.submitting")
                    : t("marketing.contact.form.submit")}
                </Button>
              </form>
            )}

            <div className="mt-10">
              <h3 className="dateline mb-3">{t("marketing.contact.faq.heading")}</h3>
              <Accordion type="single" collapsible className="border-y border-border">
                {FAQ_KEYS.map((k) => (
                  <AccordionItem key={k} value={k} className="border-b border-border last:border-b-0">
                    <AccordionTrigger className="py-3 text-left text-sm font-medium">
                      {t(`marketing.contact.faq.items.${k}.q`)}
                    </AccordionTrigger>
                    <AccordionContent className="pb-3 text-sm leading-relaxed text-muted-foreground">
                      {t(`marketing.contact.faq.items.${k}.a`)}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>

          {/* Why us */}
          <aside className="lg:col-span-3">
            <h2 className="font-serif text-xl font-semibold text-foreground">
              {t("marketing.contact.why.heading")}
            </h2>
            <ul className="mt-4 space-y-5">
              {WHY_KEYS.map((k) => (
                <li key={k} className="border-l-2 border-primary pl-4">
                  <p className="text-sm font-semibold text-foreground">
                    {t(`marketing.contact.why.items.${k}.title`)}
                  </p>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                    {t(`marketing.contact.why.items.${k}.body`)}
                  </p>
                </li>
              ))}
            </ul>
          </aside>

          {/* Contact methods */}
          <aside className="lg:col-span-2">
            <h2 className="font-serif text-xl font-semibold text-foreground">
              {t("marketing.contact.info.heading")}
            </h2>
            <ul className="mt-4 space-y-5 text-sm">
              <li className="flex items-start gap-3">
                <Mail className="mt-0.5 h-4 w-4 text-primary" aria-hidden />
                <div>
                  <p className="font-medium text-foreground">{t("marketing.contact.info.emailLabel")}</p>
                  <a href="mailto:contact@delapresse.fr" className="text-muted-foreground hover:text-foreground">
                    contact@delapresse.fr
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="mt-0.5 h-4 w-4 text-primary" aria-hidden />
                <div>
                  <p className="font-medium text-foreground">{t("marketing.contact.info.phoneLabel")}</p>
                  <span className="text-muted-foreground">{t("marketing.contact.info.phoneValue")}</span>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-4 w-4 text-primary" aria-hidden />
                <div>
                  <p className="font-medium text-foreground">{t("marketing.contact.info.addressLabel")}</p>
                  <span className="whitespace-pre-line text-muted-foreground">
                    {t("marketing.contact.info.addressValue")}
                  </span>
                </div>
              </li>
            </ul>

            <div className="mt-6 border-t border-border pt-6">
              <h3 className="dateline mb-2">{t("marketing.contact.info.socialHeading")}</h3>
              <ul className="space-y-1 text-sm">
                <li>
                  <a
                    href="https://www.linkedin.com/company/delapresse"
                    className="text-muted-foreground hover:text-foreground"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    LinkedIn
                  </a>
                </li>
                <li>
                  <a
                    href="https://x.com/delapresse"
                    className="text-muted-foreground hover:text-foreground"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    X (Twitter)
                  </a>
                </li>
              </ul>
            </div>
          </aside>
        </div>
      </motion.div>
    </div>
  );
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
    </div>
  );
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="text-xs text-destructive">{message}</p>;
}

function SuccessCard({ onReset }: { onReset: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border border-border bg-card p-8 text-center">
      <CheckCircle2 className="mx-auto h-10 w-10 text-primary" aria-hidden />
      <h2 className="mt-4 font-serif text-2xl font-semibold text-foreground">
        {t("marketing.contact.success.heading")}
      </h2>
      <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-muted-foreground">
        {t("marketing.contact.success.body")}
      </p>
      <p className="mt-3 text-sm text-foreground">
        <strong>{t("marketing.contact.success.responseTime")}</strong>
      </p>
      <div className="mt-6 flex flex-col items-center justify-center gap-2 sm:flex-row sm:gap-4">
        <Link to="/ressources#faq" className="inline-flex items-center text-sm text-primary hover:underline">
          {t("marketing.contact.success.faqLink")} <ArrowRight className="ml-1 h-3 w-3" />
        </Link>
        <button onClick={onReset} className="text-sm text-muted-foreground hover:text-foreground">
          {t("marketing.contact.success.sendAnother")}
        </button>
      </div>
    </div>
  );
}
